import moment from 'moment';

class OptionsController {

    constructor(basket, checkout, optionExtrasService, markdownService, guestnameService, messageService) {
        this.basket = basket;
        this.checkout = checkout;
        this.optionExtrasService = optionExtrasService;
        this.markdownService = markdownService;
        this.guestnameService = guestnameService;
        this.messageService = messageService;
    }

    $onInit () {
        this.hasOptions = false;
        this.optionsLoading = true;
        this.display = [];
        this.displayDropdown = null;
        this.optionExtras = {
            'SpaDay': {},
            'Treatment': {}
        };

        return this.basket.promise.then(() => {
            this.transformCurrentOptions();
            this.getOptionsAndExtras();
            this.checkout.setStepByName('customise');
        });
    }

    getOptionsAndExtras () {
        this.basket.saving = true;

        return this.basket.promise.then(() => {
            return Promise.all([this.collectOptions(), this.collectExtras()]);
        });
    }

    collectOptions () {
        return this.optionExtrasService.collectOptions(this.optionExtras)
            .then(response => {
                if (Object.keys(response.Treatment).length > 0 ||
                    Object.keys(response.SpaDay).length > 0
                ) {
                    this.hasOptions = true;
                }

                this.checkout.options = response;
                this.optionsLoading = false;
                this.basket.saving = false;
            });
    }

    collectExtras () {
        return this.optionExtrasService.collectExtras(this.optionExtras)
            .then(response => {
                if (Object.keys(response.Treatment).length > 0 ||
                    Object.keys(response.SpaDay).length > 0
                ) {
                    this.hasOptions = true;
                }

                this.checkout.extras = response;
                this.optionsLoading = false;
                this.basket.saving = false;
            });
    }

    guestHasChoices (guest) {
        let hasOptions = false;
        for (let item of guest.item_details) {
            if (this.eventHasChoices(item, guest.id)) {
                item.requiredOptions = 0;
                if (this.checkout.options
                    && this.checkout.options[item.item_summary.type_name]
                    && this.checkout.options[item.item_summary.type_name][item.item_summary.id]
                    && this.checkout.options[item.item_summary.type_name][item.item_summary.id].OptionGroupOption) {
                    this.checkout.options[item.item_summary.type_name][item.item_summary.id].OptionGroupOption.forEach(group => {
                        item.requiredOptions += group.selections;
                    });
                }
                hasOptions = true;
            }
        }
        return hasOptions;
    }

    eventHasChoices (event) {
        if ((this.checkout.options && Object.keys(this.checkout.options[event.item_summary.type_name]).indexOf(event.item_summary.id.toString()) > -1) ||
            (this.checkout.extras && Object.keys(this.checkout.extras[event.item_summary.type_name]).indexOf(event.item_summary.id.toString()) > -1)
        ) {
            return true;
        }

        return false;
    }

    addOption (guest, event, optionGroup, option, doSubmit = true) {
        let guestEvent = guest.item_details.find(item => item.reference === event.reference);

        if (!this.hasOption(guestEvent, option)) {
            if (guestEvent.item_children && optionGroup.selections === 1) {
                this.removeExistingOption(guestEvent, option);
            }

            if (this.countSelectedOptions(guest, event, optionGroup) < optionGroup.selections) {
                option.date = guestEvent.date;
                guestEvent.item_children.push(this.basket.assignItem(option));

                if (doSubmit) {
                    return this.submitBasket();
                }

                return;
            }

            this.messageService.add('Maximum selections chosen');
            this.messageService.failure();
            return;
        }

        this.removeExistingOption(guestEvent, option, true);

        if (doSubmit) {
            return this.submitBasket();
        }
    }

    removeExistingOption (guestEvent, option, findOption = false) {
        let existingOption = this.findOptionIndex(guestEvent, option, findOption);

        if (existingOption > -1) {
            guestEvent.item_children.splice(existingOption, 1);

            if (guestEvent.credit_state) {
                guestEvent.credit_state.splice(existingOption, 1);
            }
        }
    }

    hasOption (guestEvent, option) {
        return this.findOptionIndex(guestEvent, option, true) > -1;
    }

    guestHasOption (guest, option) {
        return !!guest.item_details.find(item => this.hasOption(item, option));
    }

    findOptionIndex (guestEvent, option, findOption = false) {
        return guestEvent.item_children.findIndex(child => {
            if (child.item_type !== 'App\\OptionGroupOption') {
                return false;
            }

            if (findOption) {
                if (child.item_summary) {
                    return child.item_summary.id === option.id;
                }
                return child.item_id === option.id;
            }
            return !!(child.item && child.item.option_group_id === parseInt(option.group_id));
        });
    }

    hasExtra (guest, event, extra, boolean = true) {
        if (guest) {
            let item = guest.item_details.find(item => item.reference === event.reference);
            let foundExtra = item.item_children.findIndex(child => child.item_type === 'App\\EventExtra' && child.item_id === extra.id);

            if (boolean) {
                return foundExtra > -1;
            }

            return foundExtra;
        }
    }

    addExtra (guest, event, extra, doSubmit = true) {
        let guestEvent = guest.item_details.find(item => item.reference === event.reference);

        let existingExtra = this.hasExtra(guest, guestEvent, extra, false);
        if (existingExtra > -1) {
            guestEvent.item_children.splice(existingExtra, 1);
        } else {
            extra.type = extra.type_name;
            guestEvent.item_children.push(this.basket.assignItem(extra));
        }

        if (doSubmit) {
            return this.submitBasket();
        }
    }

    submitBasket (basketOption = 'delete_orders') {
        this.ignoreFeatured = true;
        return this.basket.submit(basketOption)
            .then(() => {
                this.displayDropdown = null;
            });
    }

    switchVisible (guestId, type, eventId = null, group = null, single = null) {
        let visibleId = `${guestId}-${type}`;
        if (eventId) {
            visibleId += `-${eventId}`;
        }
        if (group) {
            visibleId += `-${group}`;
        }
        if (single) {
            if (this.displayDropdown === visibleId) {
                this.displayDropdown = null;
                return;
            }
            this.displayDropdown = visibleId;
            return;
        }

        let existing = this.display.indexOf(visibleId);
        if (existing > -1) {
            if (this.display.length === 1) {
                this.display = [];
                return;
            }
            this.display.splice(existing, 1);
            return;
        }

        this.display.push(visibleId);
    }

    switchVisibleOn (guestId, type, eventId, group) {
        if (!this.ignoreFeatured) {
            const visibleId = `${guestId}-${type}-${eventId}-${group}`;
            if (this.display.indexOf(visibleId) === -1) {
                this.display.push(visibleId);
            }
        }
    }

    isDisplayed (guestId, type, eventId = null, group = null, single = null) {
        // Error override
        if (this.checkout.optionError && group === 'options' && single === null) {
            return false;
        }

        let visibleId = `${guestId}-${type}`;
        if (eventId) {
            visibleId += `-${eventId}`;
        }
        if (group) {
            visibleId += `-${group}`;
        }
        if (single) {
            return this.displayDropdown === visibleId;
        }

        return this.display.indexOf(visibleId) > -1;
    }

    referenceGuests (reference) {
        return this.basket.contents.guest_details.filter(guest => {
            return guest.item_details.some(guest => guest.reference === reference);
        });
    }

    countSelected (event, type) {
        switch (type) {
            case 'option':
                if (event.item_children) {
                    return event.item_children.filter(child => child.item_type === 'App\\OptionGroupOption').length;
                }
                break;

            case 'extra-free':
                if (event.item_children) {
                    return event.item_children.filter(child => {
                        let price = child.price;

                        if (child.item_summary) {
                            price = child.item_summary.price;
                        }

                        price = parseInt(price.price);
                        return child.item_type === 'App\\EventExtra' && price === 0;
                    }).length;
                }
                break;

            case 'extra-paid':
                if (event.item_children) {
                    return event.item_children.filter(child => {
                        let price = child.price;

                        if (child.item_summary) {
                            price = child.item_summary.price;
                        }

                        price = parseInt(price.price);
                        return child.item_type === 'App\\EventExtra' && price > 0;
                    }).length;
                }
                break;
        }

        return 0;
    }

    countMaximum (eventType, id, choiceType, choiceId = null) {
        if (this.checkout.options
            && this.checkout.options[eventType]
            && this.checkout.options[eventType][id]
            && this.checkout.options[eventType][id][choiceType]) {
            let number;
            switch (choiceType) {
                case 'OptionGroupOption':
                    number = this.checkout.options[eventType][id][choiceType].reduce((accumulator, choice) => {
                        return accumulator + choice.selections;
                    }, 0);
                    break;
                default:
                    number = this.checkout.options[eventType][id][choiceType].length;
                    break;
            }
            return `/${number}`;
        }
    }

    selectedMaximum (chosen, maximum) {
        if (this.checkout.optionError) {
            if ((`/${chosen}`) === maximum) {
                return true;
            }
            return false;
        }
        return true;
    }

    markdown (text) {
        return this.markdownService.translate(text);
    }

    presentGuest (guest) {
        return this.guestnameService.format(guest, true, 'experience', guest.item_count);
    }

    transformCurrentOptions () {
        for (let guest of this.basket.contents.guest_details) {

            for (let event of guest.item_details) {

                if (Object.keys(this.optionExtras[event.item_summary.type_name]).indexOf(event.item_id) === -1) {
                    this.optionExtras[event.item_summary.type_name][event.item_id] = event.item_summary.id;
                }
            }
        }
    }

    presentDate (date) {
        return moment(date).format('Do MMMM, YYYY');
    }

    guestHasChildTreatment (extra, guest) {
        return guest.item_details.find(item => {
            if (item.item_children) {
                return item.item_children.find(itemChild => itemChild.item_type === 'App\\Treatment' && itemChild.item_id === extra.treatment_id);
            }
        });
    }

    hasDescription (extra) {
        return !!(extra.description && extra.description !== 'null');
    }

    displayDescription (description) {
        if (description === 'null') {
            return '';
        }

        return this.markdown(description);
    }

    countSelectedOptions (guest, event, optionGroup) {
        let count = 0;
        const guestEvent = guest.item_details.find(item => item.reference === event.reference);

        if (guestEvent) {
            count = guestEvent.item_children.filter(child => {
                return child.item_type === 'App\\OptionGroupOption'
                    && child.item
                    && child.item.option_group_id === optionGroup.id;
            }).length;
        }

        return count;
    }
}

OptionsController.$inject = ['basket', 'checkout', 'optionExtrasService', 'markdownService', 'guestnameService', 'messageService'];

export default OptionsController;



// WEBPACK FOOTER //
// ./frontend/storefront/app/modules/checkout/controllers/options.controller.js